import React, {useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {useDebounce} from '@uidotdev/usehooks'
import {Popover, PopoverTrigger} from '@/components/ui/popover'
import {BuilderPopoverContent as PopoverContent} from '../popover/popoverContent'
import modelMap from 'hec/builder/model-map'
import Icon from 'hec/builder/blocks/icon-picker/icon-picker'
import {PopoverContext} from '../popover/popoverContext'

const PopoverComponent = ({
    children,
    attributes,
    popoverContent,
    popoverAlignOffset,
    popoverAlign,
    useArrowIcon,
    iconName
}) => {
    const [open, setOpen] = useState(false)
    const debouncedOpen = useDebounce(open, 200)
    const popoverTriggerRef = useRef(null)

    const useIcon = useArrowIcon && iconName

    useEffect(() => {
        if (popoverTriggerRef.current && useIcon) {
            const svg = popoverTriggerRef.current.querySelector(`[data-icon-name="${iconName}"]`)
            if (svg) {
                svg.style.transform = open ? 'rotate(180deg)' : 'rotate(0deg)'
                svg.style.transition = 'transform 0.3s ease'
            }
        }
    }, [open, useIcon, iconName])

    const handleMouseLeave = () => {
        const firstDrawer = document.querySelector('div[data-vaul-drawer]')
        if (firstDrawer) return
        setOpen(false)
    }

    return (
        <PopoverContext.Provider
            value={{
                popoverOpen: open,
                setPopoverOpen: setOpen,
                handleMouseLeave
            }}
        >
            <Popover
                open={debouncedOpen}
                onOpenChange={setOpen}
                data-testid="popoverComponent-Popover-deprecated"
            >
                <PopoverTrigger
                    ref={popoverTriggerRef}
                    onMouseEnter={() => setOpen(true)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => setOpen(!open)}
                    className="flex items-center justify-center space-x-2"
                    data-testid={'popover-deprecated-PopoverTrigger'}
                >
                    {children}
                    <Icon
                        iconName={iconName}
                        data-icon-name={iconName}
                        data-testid={'popover-deprecated-Icon'}
                    />
                </PopoverTrigger>
                <PopoverContent
                    popoverContent={popoverContent}
                    alignOffset={popoverAlignOffset}
                    align={popoverAlign}
                    {...attributes}
                    data-testid={'popover-deprecated-PopoverContent'}
                ></PopoverContent>
            </Popover>
        </PopoverContext.Provider>
    )
}

PopoverComponent.propTypes = {
    children: PropTypes.node,
    attributes: PropTypes.any,
    popoverContent: PropTypes.any,
    popoverAlignOffset: PropTypes.number,
    popoverAlign: PropTypes.string,
    useArrowIcon: PropTypes.bool,
    iconName: PropTypes.string
}

export const PopoverDefinition = {
    component: PopoverComponent,
    name: 'Popover',
    hideFromInsertMenu: true,
    noWrap: true,
    canHaveChildren: true,
    shouldReceiveBuilderProps: {
        builderBlock: true
    },
    defaultChildren: [
        {
            '@type': '@builder.io/sdk:Element',
            component: {
                name: 'Text',
                options: {text: 'log In'}
            }
        },
        {
            '@type': '@builder.io/sdk:Element',
            component: {
                name: 'IconPicker',
                options: {
                    iconName: 'User'
                }
            }
        }
    ],
    inputs: [
        {
            name: 'popoverAlignOffset',
            type: 'number',
            showIf: `options.get('popoverAlign') === 'start' || options.get('popoverAlign') === 'end'`
        },
        {
            name: 'popoverAlign',
            enum: ['start', 'end', 'center']
        },
        {
            name: 'popoverContent',
            type: 'reference',
            model: modelMap.popoverContent
        },
        {
            name: 'useArrowIcon',
            type: 'boolean',
            defaultValue: false,
            helperText: 'An arrow icon that will rotate when the popup is expanded.'
        },
        {
            name: 'iconName',
            type: 'string',
            enum: ['ArrowDown'],
            defaultValue: 'ArrowDown',
            showIf: `options.get('useArrowIcon') === true`
        }
    ]
}
